import { db } from '../../firebase.js';
import {
  collection,
  query,
  where,
  getDocs,
  getDoc,
  setDoc,
  deleteDoc,
  doc,
  updateDoc,
  writeBatch,
} from 'firebase/firestore';
import ImageUpload from '../ImageUpload';
import { v4 } from 'uuid';
import NotificationService from '../notifications/NotificationService';
import {
  ADMIN_FEES_PERCENT_MIN,
  ADMIN_FEES_PERCENT_MAX,
  ATTENDEE_LIMIT,
} from '../../constants/event.constants';
import {
  AdminFees_schema,
  Attendees_schema,
  EventDataSchema,
  eventToSchema,
} from '../Schemas/EventSchema';
import { Event, schemaToEvent } from '../../models/event';
import * as ERROR_MESSAGES from '../../constants/error-messages.constants';

const notifService = new NotificationService();

class EventService {
  private dbName = process.env.DB === 'TEST' ? 'events_test' : 'events';

  /* EVENT DATA FUNCTIONS */
  validateAdminFees(adminFees: AdminFees_schema): AdminFees_schema {
    let totalPercent = 0;

    for (const payee in adminFees) {
      const percent = adminFees[payee].percent;

      // Check if percent is between ADMIN_FEES_PERCENT_MIN and ADMIN_FEES_PERCENT_MAX
      if (
        percent < ADMIN_FEES_PERCENT_MIN ||
        percent > ADMIN_FEES_PERCENT_MAX
      ) {
        throw new Error(ERROR_MESSAGES.ADMIN_FEES_PERCENT_MIN_MAX);
      }

      totalPercent += percent;
    }

    // Check if total percent is between ADMIN_FEES_PERCENT_MIN and ADMIN_FEES_PERCENT_MAX
    if (
      totalPercent < ADMIN_FEES_PERCENT_MIN ||
      totalPercent > ADMIN_FEES_PERCENT_MAX
    ) {
      throw new Error(ERROR_MESSAGES.ADMIN_FEES_SUM_MIN_MAX);
    }

    if (totalPercent === 0) {
      return {};
    }

    return adminFees;
  }

  validateBlindStructure(
    blindStructure: {
      level: string;
      smallBlind: number;
      bigBlind: number;
      ante: number;
      duration: number;
    }[]
  ): {} {
    const blindStructureObject: {
      [key: string]: {
        level: string;
        duration: number;
        small_blind?: number;
        big_blind?: number;
        ante?: number;
      };
    } = {};

    if (blindStructure && blindStructure.length > 0) {
      blindStructure.forEach((level, index) => {
        const levelKey = index + 1;
        if (level.level === 'break') {
          if (isNaN(level.duration) || level.duration <= 0) {
            throw new Error(
              'Each break level must have a duration greater than 0.'
            );
          }
          blindStructureObject[levelKey] = {
            level: level.level,
            duration: level.duration,
          };
        } else {
          if (
            isNaN(level.smallBlind) ||
            level.smallBlind <= 0 ||
            isNaN(level.bigBlind) ||
            level.bigBlind <= 0 ||
            isNaN(level.duration) ||
            level.duration <= 0 ||
            (level.ante && isNaN(level.ante)) ||
            level.ante < 0
          ) {
            throw new Error(ERROR_MESSAGES.BLIND_STRUCTURE_MIN_MAX);
          }
          blindStructureObject[levelKey] = {
            level: level.level,
            small_blind: level.smallBlind,
            big_blind: level.bigBlind,
            ante: level.ante,
            duration: level.duration,
          };
        }
      });
    }

    return blindStructureObject;
  }

  async createEvent(userId: any, event: Event) {
    if (!event) {
      throw new Error(ERROR_MESSAGES.EVENT_REQUIRED);
    }

    try {
      const { imageFile } = event;
      let imageUrl;
      const eventSchema = eventToSchema(event, userId);
      if (
        eventSchema.attendees &&
        Object.keys(eventSchema.attendees).length > ATTENDEE_LIMIT
      ) {
        throw new Error(ERROR_MESSAGES.ATTENDEE_MAX);
      }
      if (imageFile && imageFile.length > 0) {
        const image = imageFile[0];
        imageUrl = await this.createEventImageUpload(image, userId);
        eventSchema.image_url = imageUrl;
      }
      const eventRef = doc(collection(db, this.dbName));
      const batch = writeBatch(db);

      // Strip undefined fields — Firestore rejects undefined values
      const cleanSchema = Object.fromEntries(
        Object.entries(eventSchema).filter(([_, v]) => v !== undefined)
      );

      // Add the event to the batch
      batch.set(eventRef, cleanSchema);

      // Add attendee data to the firestore attendee subcollection in the event document
      const attendees = eventSchema.attendees;
      for (const [attendeeId, attendee] of Object.entries(attendees)) {
        const attendeeSubCollectionRef = doc(eventRef, 'attendees', attendeeId);
        batch.set(attendeeSubCollectionRef, {
          method: attendee.method,
          status: attendee.status,
        });
      }

      // // Commit the batch
      await batch.commit();
      console.log('Event created successfully.');
      // Send dealer invite if assigned
      if (eventSchema.dealer_uid && eventSchema.dealer_status === 'none') {
        await updateDoc(eventRef, { dealer_status: 'pending' });
        try {
          const userSnap = await getDoc(doc(db, 'users', userId));
          const tdName = userSnap.exists() ? (userSnap.data().display_name ?? 'A Tournament Director') : 'A Tournament Director';
          const hoursUntil = eventSchema.dealer_invite_deadline
            ? (new Date(eventSchema.dealer_invite_deadline).getTime() - Date.now()) / 3600000
            : 24;
          // Increment dealer invite_count
          try {
            const dealerRef = doc(db, 'dealers', eventSchema.dealer_uid!);
            const dealerSnap = await getDoc(dealerRef);
            if (dealerSnap.exists()) {
              const dd = dealerSnap.data();
              const newCount = (dd.invite_count ?? 0) + 1;
              const responseCount = dd.response_count ?? 0;
              await updateDoc(dealerRef, {
                invite_count: newCount,
                response_rate: Math.round((responseCount / newCount) * 100),
              });
            }
          } catch {}
          await notifService.notifyDealerInvited({
            dealerUid: eventSchema.dealer_uid,
            tdName,
            eventId: eventRef.id,
            eventName: eventSchema.name ?? 'Poker Event',
            deadline: eventSchema.dealer_invite_deadline ?? new Date(Date.now() + 86400000).toISOString(),
            urgent: hoursUntil < 6,
          });
        } catch (e) { console.error('Dealer invite notification failed:', e); }
      }
      return eventRef.id; // Return the document ID
    } catch (error) {
      console.error('Error creating event:', error);
      throw error;
    }
  }

  // get an event by event document id
  async getEvent(eventId: string): Promise<Event> {
    try {
      const docRef = doc(db, this.dbName, eventId);
      const docSnap = await getDoc(docRef);

      const attendeesRef = collection(docRef, 'attendees');
      const attendeesSnapshot = await getDocs(attendeesRef);
      const attendees = attendeesSnapshot.docs.map((doc) => ({
        user_id: doc.id,
        ...doc.data(),
      }));

      const eventData = docSnap.data();
      if (!eventData) {
        throw new Error(ERROR_MESSAGES.EVENT_NOT_FOUND(eventId));
      }
      if (eventData) {
        eventData.attendees = attendees;
      }

      const eventRet = schemaToEvent(
        { ...eventData } as EventDataSchema,
        eventId
      );
      return eventRet as Event;
    } catch (error) {
      console.warn(error);
      throw new Error(ERROR_MESSAGES.GET_EVENT(eventId));
    }
  }

  // update an event by event document id
  async updateEvent(userId: string, eventId: string, eventData: Event) {
    try {
      const eventSchema = eventToSchema(eventData, userId);
      if (
        eventSchema.attendees &&
        Object.keys(eventSchema.attendees).length > ATTENDEE_LIMIT
      ) {
        throw new Error(ERROR_MESSAGES.ATTENDEE_MAX);
      }
      let imageUrl;
      const { imageFile } = eventData;
      if (imageFile && imageFile.length > 0) {
        const image = imageFile[0];
        if (eventData.imageUrl) {
          // edit existing image
          imageUrl = await this.updateEventImageUpload(
            image,
            eventData.imageUrl
          );
        } else {
          // create image
          imageUrl = await this.createEventImageUpload(image, userId);
        }
        eventSchema.image_url = imageUrl;
      }

      const eventRef = doc(db, this.dbName, eventId);
      await updateDoc(eventRef, eventSchema);
    } catch (error) {
      console.error('Error updating event:', error);
      throw error;
    }
  }

  // get all events for a user by their uid
  async getUserEvents(
    uid: string
  ): Promise<{ id: any; data: EventDataSchema }[]> {
    try {
      const q = query(
        collection(db, this.dbName),
        where('admin_uid', '==', uid)
      );
      const querySnapshot = await getDocs(q);
      let userEvents: any = [];
      querySnapshot.forEach((doc) => {
        userEvents.push({ id: doc.id, data: doc.data() });
      });
      return userEvents;
    } catch (error) {
      console.error('Error fetching user data:', error);
      throw error;
    }
  }

  async getPublicEvents(): Promise<any> {
    try {
      const q = query(
        collection(db, this.dbName),
        where('privacy', '==', 'public')
      );
      const querySnapshot = await getDocs(q);
      let publicEvents: any = [];
      querySnapshot.forEach((doc) => {
        publicEvents.push({ id: doc.id, data: doc.data() });
      });
      return publicEvents;
    } catch (error) {
      console.error('Error fetching user data:', error);
      return null;
    }
  }

  // delete an event by event document id
  async deleteEvent(eventId: string) {
    try {
      const eventRef = doc(db, this.dbName, eventId);
      const attendeesRef = collection(eventRef, 'attendees');
      const attendeesSnapshot = await getDocs(attendeesRef);

      // Prepare the batch
      const batch = writeBatch(db);

      // Add all attendees to the batch for deletion
      attendeesSnapshot.forEach((attendeeDoc) => {
        batch.delete(doc(attendeesRef, attendeeDoc.id));
      });

      // Add the event to the batch for deletion
      batch.delete(eventRef);

      // Commit the batch
      await batch.commit();
      console.log('Event and attendees deleted successfully.');
    } catch (error) {
      console.error('Error deleting event and attendees:', error);
      throw error;
    }
  }

  // upload an image for an event
  async createEventImageUpload(image: File, uid: string): Promise<string> {
    try {
      const imagePath = `${uid}/images/event-images/${v4()}/${image.name}`;
      const imageURL = await ImageUpload(image, imagePath);
      return imageURL;
    } catch (error) {
      console.error('Error uploading event image:', error);
      throw error;
    }
  }

  // Update existing event image

  async updateEventImageUpload(image: File, imageUrl: string): Promise<string> {
    try {
      const imagePath = imageUrl;
      const imageURL = await ImageUpload(image, imagePath);
      return imageURL;
    } catch (error) {
      console.error('Error uploading event image:', error);
      throw error;
    }
  }

  // ATTENDEE DATA FUNCTIONS

  // sets an attendee to an event by event document id
  // works for both admin and user
  // This can also be used to update an attendee's status
  async setAttendee(eventId: string, attendee: any) {
    try {
      // Fetch the current number of attendees
      const attendeesSnapshot = await getDocs(
        collection(db, `events/${eventId}/attendees`)
      );
      const currentAttendeesCount = attendeesSnapshot.size;

      if (1 + currentAttendeesCount > ATTENDEE_LIMIT) {
        throw new Error(
          `Cannot have more than ${ATTENDEE_LIMIT} attendees for an event.`
        );
      }

      const attendeeRef = doc(
        db,
        this.dbName,
        eventId,
        'attendees',
        attendee.user_id
      );
      await setDoc(attendeeRef, {
        method: attendee.method,
        status: attendee.status,
      });
      console.log('Attendee added successfully.');
    } catch (error) {
      console.error('Error adding attendee:', error);
      throw error;
    }
  }

  async setAttendees(eventId: string, attendees: Attendees_schema) {
    if (Object.keys(attendees).length > ATTENDEE_LIMIT) {
      throw new Error(ERROR_MESSAGES.SET_ATTENDEE_LIMIT);
    }

    try {
      // Fetch the current number of attendees
      const attendeesSnapshot = await getDocs(
        collection(db, `events/${eventId}/attendees`)
      );
      const currentAttendeesCount = attendeesSnapshot.size;

      if (
        Object.keys(attendees).length + currentAttendeesCount >
        ATTENDEE_LIMIT
      ) {
        throw new Error(ERROR_MESSAGES.ATTENDEE_MAX);
      }

      const batch = writeBatch(db);

      // Add each attendee to the batch
      for (const [userId, attendee] of Object.entries(attendees)) {
        const attendeeRef = doc(db, this.dbName, eventId, 'attendees', userId);
        batch.set(attendeeRef, attendee);
      }

      // Commit the batch
      await batch.commit();

      console.log('Attendees added successfully.');
    } catch (error) {
      console.error('Error adding attendees:', error);
      throw error;
    }
  }

  // remove an attendee from an event by event id
  // works for only admin
  async deleteAttendee(eventId: string, attendeeId: string) {
    try {
      await deleteDoc(doc(db, this.dbName, eventId, 'attendees', attendeeId));
      console.log('Attendee deleted successfully.');
    } catch (error) {
      console.error('Error deleting attendee:', error);
      throw error;
    }
  }

  // get an attendee from an event by event document id
  async getAttendee(eventId: string, attendeeId: string): Promise<any> {
    try {
      const docRef = doc(db, this.dbName, eventId, 'attendees', attendeeId);
      const docSnap = await getDoc(docRef);
      return docSnap.data();
    } catch (error) {
      console.error('Error getting attendee:', error);
      throw error;
    }
  }

  // get all attendees from an event by event document id
  async getAttendees(eventId: string): Promise<any> {
    try {
      const q = query(collection(db, this.dbName, eventId, 'attendees'));
      const querySnapshot = await getDocs(q);
      let attendees: any = [];
      querySnapshot.forEach((doc) => {
        attendees.push({ user_id: doc.id, data: doc.data() });
      });
      return attendees;
    } catch (error) {
      console.error('Error getting attendees:', error);
      throw error;
    }
  }

  // DEPRECATED FUNCTIONS
  async createEvent_DEPRECATE(eventData: EventDataSchema) {
    if (!eventData) {
      throw new Error('Event data is required.');
    }

    if (
      eventData.attendees &&
      Object.keys(eventData.attendees).length > ATTENDEE_LIMIT
    ) {
      throw new Error(
        `Cannot have more than ${ATTENDEE_LIMIT} attendees for an event.`
      );
    }

    try {
      const preparedData = this.prepareEventData_DEPRECATE(eventData);
      const eventRef = doc(collection(db, this.dbName));
      const batch = writeBatch(db);

      // Add the event to the batch
      batch.set(eventRef, preparedData);

      // Add attendee data to the firestore attendee subcollection in the event document
      const attendees = eventData.attendees;
      for (const [attendeeId, attendee] of Object.entries(attendees)) {
        const attendeeSubCollectionRef = doc(eventRef, 'attendees', attendeeId);
        batch.set(attendeeSubCollectionRef, {
          method: attendee.method,
          status: attendee.status,
        });
      }

      // Commit the batch
      await batch.commit();

      console.log('Event created successfully.');
      return eventRef.id; // Return the document ID
    } catch (error) {
      console.error('Error creating event:', error);
      throw error;
    }
  }

  // get an event by event document id
  async getEvent_DEPRECATE(eventId: string): Promise<any> {
    try {
      const docRef = doc(db, this.dbName, eventId);
      const docSnap = await getDoc(docRef);

      const attendeesRef = collection(docRef, 'attendees');
      const attendeesSnapshot = await getDocs(attendeesRef);
      const attendees = attendeesSnapshot.docs.map((doc) => ({
        user_id: doc.id,
        ...doc.data(),
      }));

      const eventData = docSnap.data();
      if (eventData) {
        eventData.attendees = attendees;
      }
      return eventData;
    } catch (error) {
      console.log('Error retrieving event data:', error);
    }
  }

  // update an event by event document id
  async updateEvent_DEPRECATE(eventId: string, eventData: any) {
    try {
      const preparedData = this.prepareEventData_DEPRECATE(eventData);

      const eventRef = doc(db, this.dbName, eventId);
      await updateDoc(eventRef, preparedData);
      console.log('Event updated successfully.');
    } catch (error) {
      console.error('Error updating event:', error);
      throw error;
    }
  }

  // add the return type
  prepareEventData_DEPRECATE(eventData: any): EventDataSchema {
    const blindStructureObject = this.validateBlindStructure(
      eventData.blind_structure
    );
    const adminFees = this.validateAdminFees(eventData.admin_fees);

    return {
      admin_uid: eventData.admin_uid,
      image_url: eventData.image_url,
      name: eventData.name,
      desc: eventData.desc,
      start: eventData.start,
      timezone: eventData.timezone,
      location: eventData.location,
      privacy: eventData.privacy,
      currency: eventData.currency,
      admin_fees: adminFees,
      game_limit: eventData.game_limit,
      game_type: eventData.game_type,
      max_players: parseInt(eventData.max_players),
      buy_in: parseInt(eventData.buy_in),
      blind_level_duration: parseInt(eventData.blind_level_duration),
      blind_structure: blindStructureObject,
      rebuy:
        Object.keys(eventData.rebuy || {}).length === 0
          ? {}
          : {
              rebuys_allowed: parseInt(eventData.rebuy?.rebuys_allowed),
              rebuy_cutoff: parseInt(eventData.rebuy?.rebuy_cutoff),
              rebuy_price: parseInt(eventData.rebuy?.rebuy_price),
              rebuy_chips: parseInt(eventData.rebuy?.rebuy_chips),
            },
      add_on:
        Object.keys(eventData.add_on || {}).length === 0
          ? {}
          : {
              add_on_price: parseInt(eventData.add_on?.add_on_price),
              add_on_chips: parseInt(eventData.add_on?.add_on_chips),
            },
      starting_chips: parseInt(eventData.starting_chips),
      big_blind_ante_round_start: parseInt(
        eventData.big_blind_ante_round_start
      ),
      late_entry_cutoff: parseInt(eventData.late_entry_cutoff),
      reentry:
        Object.keys(eventData.reentry || {}).length === 0
          ? {}
          : {
              reentries: parseInt(eventData.reentry?.reentries),
              reentry_cutoff: parseInt(eventData.reentry?.reentry_cutoff),
              reentry_price: parseInt(eventData.reentry?.reentry_price),
            },
      bounty:
        Object.keys(eventData.bounty || {}).length === 0
          ? {}
          : {
              bounty_amount: parseInt(eventData.bounty?.bounty_amount),
              bounty_price: parseInt(eventData.bounty?.bounty_price),
            },
      attendees: eventData.attendees,
    };
  }
}

export default EventService;
